import { useState } from "react";
import { useAuth } from "./AuthContext";
import { addToCart, addComboToCart } from "../utilities/service";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";

const useAddToCart = () => {
  const { user, setUser, setLoginDialogOpen } = useAuth();
  const navigate = useNavigate();
  const [shouldNavigateToCart, setShouldNavigateToCart] = useState(false);

  const buildFormData = (data) => {
    const formData = new FormData();
    for (const key in data) {
      const value = data[key];
      if (value instanceof File) {
        formData.append(key, value);
      } else if (typeof value === "object" && value !== null) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    }
    return formData;
  };

  const dataURLtoFile = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  /**
   * Simple add-to-cart for product listing cards (ProductItem, OffersAndDeals).
   * Uses only the first variation with defaults — no bulk, no combo, no pincode.
   */
  const handleAddToCart = async (e, item) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!user?.customer_id) {
      setLoginDialogOpen(true);
      return;
    }

    const variation = item?.variation?.[0];
    const flavourId = item?.flavour?.[0]?.id;

    // Determine egg/eggless availability — mirrors ProductDetails cakeType logic
    const hasEgg = +variation?.eggOriginalPrice > 0;
    const hasEggless = +variation?.eggLessOriginalPrice > 0;

    const isEggless = !hasEgg && hasEggless; // eggless-only product
    const egglessFlag = isEggless ? 1 : 0;

    // discountPrice = what customer pays (originalPrice field in variation)
    const discountPrice = isEggless
      ? variation?.eggLessOriginalPrice
      : variation?.eggOriginalPrice;

    // originalPrice = MRP before discount (egg / eggLess field)
    const originalPrice = isEggless
      ? variation?.eggLess
      : variation?.egg;

    const param = {
      customer_id: user?.customer_id,
      product_id: item?.id,
      product_quantity: 1,
      product_size_id: variation?.weight_id,
      product_total: discountPrice,
      product_price: originalPrice,
      product_discount_price: discountPrice,
      flavour_id: flavourId,
      message_on_cake: "",
      variation_ids: variation?.variation_id,
      eggless: egglessFlag,
      is_bulk_product: 0,
      pincode: "",
    };

    try {
      const formData = buildFormData(param);
      const res = await addToCart(formData);
      if (!res?.error) {
        setUser((prev) => ({ ...prev, cartCount: res?.data?.cart_count }));
        toast.success(res?.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to add to cart");
    }
  };

  /**
   * Full add-to-cart matching ProductDetails logic.
   * Supports combo, bulk, custom image, pincode, buy-now navigation.
   *
   * @param {object} options
   * @param {object} options.productDetails       - Full product object from API
   * @param {string} options.id                   - Product ID (from URL params)
   * @param {number} options.quantity             - Selected quantity
   * @param {number} options.variationIndex       - Selected variation index
   * @param {string} options.cakeType             - "EGG" | "EGGLESS"
   * @param {string|number} options.activeTab     - Selected weight_id
   * @param {string} options.discountPrice        - Calculated discount price (as string)
   * @param {string} options.originalPrice        - Calculated original price (as string)
   * @param {number} options.discountPriceOnly    - Unit discount price (no qty multiplication)
   * @param {number|null} options.flavourId       - Selected flavour ID
  * @param {string} options.message              - Customer note or customization message
   * @param {string} options.pincode              - Delivery pincode
   * @param {number|object} options.isBulkVariant - Matched bulk price object or falsy
   * @param {string|null} options.uploadedCakeImageBase - Base64 image string or null
   * @param {boolean} options.buyNow              - If true, navigate to cart after success
   */
  const handleAddToCartFull = async ({
    productDetails,
    id,
    quantity,
    variationIndex,
    cakeType,
    activeTab,
    discountPrice,
    originalPrice,
    discountPriceOnly,
    flavourId,
    message,
    pincode,
    isBulkVariant,
    uploadedCakeImageBase = null,
    buyNow = false,
  }) => {
    if (!user?.customer_id) {
      setLoginDialogOpen(true);
      return;
    }

    if (buyNow) setShouldNavigateToCart(true);

    let param;

    if (productDetails?.combo_price > 0) {
      // --- Combo product ---
      param = {
        customer_id: user?.customer_id,
        parent_id: id,
        combo_price: productDetails?.combo_price,
        product_quantity: quantity,
        pincode: pincode,
        product_variation: productDetails?.combo_product_details?.map(
          (item) => ({
            ...item?.variation?.[variationIndex],
            product_id: item.id,
            isEggless: cakeType === "EGGLESS" ? 1 : 0,
          })
        ),
      };
    } else {
      // --- Regular product ---
      param = {
        customer_id: user?.customer_id,
        product_id: productDetails?.id,
        product_quantity: quantity,
        product_size_id: activeTab,
        product_total: discountPrice,
        product_price: originalPrice,
        product_discount_price: discountPriceOnly,
        flavour_id: flavourId,
        message_on_cake: message,
        pincode: pincode,
        variation_ids: productDetails?.variation[variationIndex]?.variation_id,
        eggless: cakeType === "EGGLESS" ? 1 : 0,
        is_bulk_product: isBulkVariant?.id || 0,
      };

      if (uploadedCakeImageBase) {
        const imageFile = dataURLtoFile(uploadedCakeImageBase, "custom_image.png");
        param.customer_refer_image = imageFile;
      }
    }

    try {
      const formData = buildFormData(param);
      const res =
        productDetails?.combo_price > 0
          ? await addComboToCart(formData)
          : await addToCart(formData);

      if (!res?.error) {
        setUser((prev) => ({ ...prev, cartCount: res?.data?.cart_count }));
        toast.success(res?.message);
        if (buyNow) {
          setShouldNavigateToCart(false);
          setTimeout(() => navigate("/cart"), 500);
        }
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to add to cart");
    }
  };

  return { handleAddToCart, handleAddToCartFull };
};

export default useAddToCart;